import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrollText, Check, X, AlertTriangle, Scale, Shield } from 'lucide-react';
import { Button } from './ui/button';

const TermsOfUseModal = ({ isOpen, onAccept, onDecline }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasCheckedBox, setHasCheckedBox] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
      setHasCheckedBox(false);
    }
  }, [isOpen]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  };

  const canAccept = hasScrolledToBottom && hasCheckedBox;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        data-testid="terms-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl"
        >
          {/* Header */}
          <div className="p-6 border-b border-border flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <ScrollText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-heading">Terms of Use</h2>
              <p className="text-sm text-muted-foreground">Please read and accept before generating</p>
            </div>
          </div>

          {/* Scrollable Content */}
          <div 
            className="flex-1 overflow-y-auto p-6 space-y-6"
            onScroll={handleScroll}
            data-testid="terms-scroll-area"
          >
            {/* Introduction */}
            <p className="text-muted-foreground">
              By using MandalaMuse to generate sacred geometry artwork, you agree to the following terms and conditions. 
              Please read them carefully before proceeding.
            </p>

            {/* Section 1: License Grant */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-lg">1. License Grant & Usage Rights</h3>
              </div>
              <div className="pl-7 space-y-2 text-sm text-muted-foreground">
                <p>
                  MandalaMuse grants you a <strong className="text-foreground">non-exclusive, worldwide, royalty-free license</strong> to use, 
                  modify, reproduce, and sell artwork generated through our platform for both personal and commercial purposes.
                </p>
                <p>
                  You may use the generated artwork for:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Personal projects and spiritual practice</li>
                  <li>Commercial products (prints, merchandise, digital products)</li>
                  <li>Marketing materials and branding elements</li>
                  <li>Social media and online content</li>
                </ul>
              </div>
            </div>

            {/* Section 2: Important Limitations */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <h3 className="font-heading text-lg">2. Important Limitations & Disclaimers</h3>
              </div>
              <div className="pl-7 space-y-3 text-sm">
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="font-medium text-amber-500 mb-2">No Exclusive Copyright</p>
                  <p className="text-muted-foreground">
                    While you have permission to use and sell artwork generated through MandalaMuse, 
                    <strong className="text-foreground"> we cannot guarantee exclusive copyright ownership</strong>. 
                    Sacred geometry patterns are based on universal mathematical principles and ancient designs.
                  </p>
                </div>
                
                <div className="p-4 bg-muted/50 rounded-lg space-y-2 text-muted-foreground">
                  <p>
                    <strong className="text-foreground">What this means for you:</strong>
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Other users may generate similar or identical patterns</li>
                    <li>You may not have legal standing to prevent others from using similar designs</li>
                    <li>We do not guarantee uniqueness of any generated artwork</li>
                    <li>Multiple users may independently create and sell similar designs</li>
                  </ul>
                </div>

                <p className="text-muted-foreground">
                  For example: If you generate a Flower of Life mandala and sell it on merchandise, 
                  another user may generate a similar design and sell comparable products. 
                  This is the nature of procedurally generated sacred geometry.
                </p>
              </div>
            </div>

            {/* Section 3: Hold Harmless */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-lg">3. Hold Harmless & Limitation of Liability</h3>
              </div>
              <div className="pl-7 space-y-3 text-sm">
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-2 text-muted-foreground">
                  <p className="font-medium text-foreground">You agree to indemnify and hold harmless:</p>
                  <p>
                    MandalaMuse, its creators, operators, affiliates, and partners from any and all claims, 
                    damages, losses, liabilities, costs, and expenses (including legal fees) arising from:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Your use of generated artwork</li>
                    <li>Any products or services you create using the artwork</li>
                    <li>Claims of copyright or trademark infringement by third parties</li>
                    <li>Any violation of third-party rights</li>
                  </ul>
                </div>

                <p className="text-muted-foreground">
                  <strong className="text-foreground">You are solely responsible</strong> for ensuring that your use of 
                  generated artwork does not infringe on existing trademarks, copyrights, or other intellectual property rights. 
                  While our patterns are based on sacred geometry, it is your responsibility to verify that your specific 
                  use case does not conflict with protected designs.
                </p>
              </div>
            </div>

            {/* Section 4: Prohibited Uses */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <X className="w-5 h-5 text-red-500" />
                <h3 className="font-heading text-lg">4. Prohibited Uses</h3>
              </div>
              <div className="pl-7 space-y-2 text-sm text-muted-foreground">
                <p>You may NOT use MandalaMuse generated artwork to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Create content that is illegal, harmful, or promotes violence</li>
                  <li>Infringe on the intellectual property rights of others</li>
                  <li>Misrepresent the artwork as being hand-crafted or one-of-a-kind unless substantially modified</li>
                  <li>Train competing AI/ML models without explicit written permission</li>
                </ul>
              </div>
            </div>

            {/* Section 5: Acceptance */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <h3 className="font-heading text-lg">5. Acceptance of Terms</h3>
              </div>
              <div className="pl-7 text-sm text-muted-foreground">
                <p>
                  By clicking "I Accept" below, you acknowledge that you have read, understood, and agree to be bound 
                  by these Terms of Use. These terms may be updated from time to time, and continued use of the service 
                  constitutes acceptance of any changes.
                </p>
              </div>
            </div>

            {/* Scroll indicator */}
            {!hasScrolledToBottom && (
              <div className="text-center text-sm text-muted-foreground animate-pulse">
                ↓ Please scroll to read all terms ↓
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-border space-y-4">
            {/* Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  checked={hasCheckedBox}
                  onChange={(e) => setHasCheckedBox(e.target.checked)}
                  className="sr-only"
                  data-testid="terms-checkbox"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  hasCheckedBox 
                    ? 'bg-primary border-primary' 
                    : 'border-muted-foreground/50 group-hover:border-primary'
                }`}>
                  {hasCheckedBox && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
              </div>
              <span className="text-sm text-muted-foreground">
                I have read and understood the Terms of Use, including the limitations on copyright exclusivity 
                and my responsibility for how I use the generated artwork.
              </span>
            </label>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onDecline}
                className="flex-1 rounded-full"
                data-testid="terms-decline-btn"
              >
                Decline
              </Button>
              <Button
                onClick={onAccept}
                disabled={!canAccept}
                className={`flex-1 rounded-full ${canAccept ? 'glow-primary' : ''}`}
                data-testid="terms-accept-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                I Accept
              </Button>
            </div>

            {!canAccept && (
              <p className="text-xs text-center text-muted-foreground">
                {!hasScrolledToBottom 
                  ? 'Please scroll to read all terms before accepting' 
                  : 'Please check the box above to confirm you have read the terms'}
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TermsOfUseModal;
